import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addCaseActivity,
  closePatientCase as closePatientCaseService,
  createPatientCase as createPatientCaseService,
  deletePatientCase as deletePatientCaseService,
  getPatientCase,
  getPatientCases,
  listCaseActivities,
  PatientCaseActivityInsert,
  PatientCaseFilters,
  PatientCaseInsert,
  PatientCaseUpdate,
  PatientCaseWithDetails,
  PatientCaseActivityWithDetails,
  setReferredBackFlag as setReferredBackFlagService,
  SetReferredBackFlagResult,
  triggerReferredBackNotification,
  updatePatientCase as updatePatientCaseService,
} from '@/services/patientCaseService';
import { useToast } from '@/hooks/use-toast';

export const usePatientCases = (filters: PatientCaseFilters = {}) => {
  return useQuery({
    queryKey: ['patientCases', filters],
    queryFn: () => getPatientCases(filters),
  });
};

export const usePatientCase = (caseId?: string) => {
  return useQuery({
    queryKey: ['patientCase', caseId],
    queryFn: () => getPatientCase(caseId as string),
    enabled: Boolean(caseId),
  });
};

export const useCaseActivities = (caseId?: string) => {
  return useQuery({
    queryKey: ['patientCaseActivities', caseId],
    queryFn: () => listCaseActivities(caseId as string),
    enabled: Boolean(caseId),
  });
};

interface UsePatientCaseMutations {
  createPatientCase: (patientCase: PatientCaseInsert) => Promise<PatientCaseWithDetails>;
  updatePatientCase: (payload: {
    caseId: string;
    updates: PatientCaseUpdate;
    currentUserId: string;
  }) => Promise<PatientCaseWithDetails>;
  closePatientCase: (payload: {
    caseId: string;
    currentUserId: string;
  }) => Promise<PatientCaseWithDetails>;
  setReferredBackFlag: (payload: {
    caseId: string;
    nextFlag: boolean;
    currentUserId: string;
    referredBackDate?: string | null;
  }) => Promise<SetReferredBackFlagResult>;
  addCaseActivity: (activity: PatientCaseActivityInsert) => Promise<PatientCaseActivityWithDetails>;
  deletePatientCase: (payload: { caseId: string; patientName?: string }) => Promise<void>;
  isCreating: boolean;
  isUpdating: boolean;
  isClosing: boolean;
  isDeleting: boolean;
  isFlippingReferredBack: boolean;
  isAddingActivity: boolean;
}

export const usePatientCaseMutations = (): UsePatientCaseMutations => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidatePatientCaseQueries = async (caseId?: string) => {
    await queryClient.invalidateQueries({ queryKey: ['patientCases'] });

    if (caseId) {
      await queryClient.invalidateQueries({ queryKey: ['patientCase', caseId] });
      await queryClient.invalidateQueries({ queryKey: ['patientCaseActivities', caseId] });
    }
  };

  const createMutation = useMutation({
    mutationFn: createPatientCaseService,
    onSuccess: async (createdCase) => {
      await invalidatePatientCaseQueries(createdCase.id);
      toast({
        title: 'Case created',
        description: `${createdCase.patient_display_name} is now tracked in Patients.`,
      });
    },
    onError: (error) => {
      console.error('Error creating patient case:', error);
      toast({
        title: 'Error',
        description: 'Failed to create patient case.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      caseId,
      updates,
      currentUserId,
    }: {
      caseId: string;
      updates: PatientCaseUpdate;
      currentUserId: string;
    }) => updatePatientCaseService(caseId, updates, currentUserId),
    onSuccess: async (updatedCase) => {
      await invalidatePatientCaseQueries(updatedCase.id);
      toast({
        title: 'Case updated',
        description: `${updatedCase.patient_display_name} has been updated.`,
      });
    },
    onError: (error) => {
      console.error('Error updating patient case:', error);
      toast({
        title: 'Error',
        description: 'Failed to update patient case.',
        variant: 'destructive',
      });
    },
  });

  const closeMutation = useMutation({
    mutationFn: ({
      caseId,
      currentUserId,
    }: {
      caseId: string;
      currentUserId: string;
    }) => closePatientCaseService(caseId, currentUserId),
    onSuccess: async (closedCase) => {
      await invalidatePatientCaseQueries(closedCase.id);
      toast({
        title: 'Case closed',
        description: `${closedCase.patient_display_name} has been moved to closed.`,
      });
    },
    onError: (error) => {
      console.error('Error closing patient case:', error);
      toast({
        title: 'Error',
        description: 'Failed to close patient case.',
        variant: 'destructive',
      });
    },
  });

  const referredBackMutation = useMutation({
    mutationFn: async ({
      caseId,
      nextFlag,
      currentUserId,
      referredBackDate,
    }: {
      caseId: string;
      nextFlag: boolean;
      currentUserId: string;
      referredBackDate?: string | null;
    }) => {
      const result = await setReferredBackFlagService(caseId, nextFlag, currentUserId, {
        referredBackDate,
      });

      // Best-effort: fire the notification edge function on a false->true
      // transition. The edge function is idempotent (24h dedup) and never
      // throws back to the UI even if the email provider fails.
      if (result.transitionedToReferredBack) {
        await triggerReferredBackNotification(caseId);
      }

      return result;
    },
    onSuccess: async (result) => {
      await invalidatePatientCaseQueries(result.patientCase.id);
      if (result.transitionedToReferredBack) {
        toast({
          title: 'Referred back',
          description: `${result.patientCase.patient_display_name} is now marked as referred back. The referring clinic will be notified.`,
        });
      } else if (result.previousReferredBackFlag && !result.patientCase.referred_back_flag) {
        toast({
          title: 'Referred-back flag cleared',
          description: `${result.patientCase.patient_display_name} is no longer marked as referred back.`,
        });
      }
    },
    onError: (error) => {
      console.error('Error toggling referred-back flag:', error);
      toast({
        title: 'Error',
        description: 'Failed to update the referred-back flag.',
        variant: 'destructive',
      });
    },
  });

  const activityMutation = useMutation({
    mutationFn: addCaseActivity,
    onSuccess: async (activity) => {
      await invalidatePatientCaseQueries(activity.case_id);
      toast({
        title: 'Activity logged',
        description: 'The patient case timeline has been updated.',
      });
    },
    onError: (error) => {
      console.error('Error adding patient case activity:', error);
      toast({
        title: 'Error',
        description: 'Failed to add patient case activity.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ caseId }: { caseId: string; patientName?: string }) =>
      deletePatientCaseService(caseId),
    onSuccess: async (_result, variables) => {
      await invalidatePatientCaseQueries(variables.caseId);
      toast({
        title: 'Referral deleted',
        description: variables.patientName
          ? `${variables.patientName} has been removed.`
          : 'The referral has been removed.',
      });
    },
    onError: (error) => {
      console.error('Error deleting patient case:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete the referral.',
        variant: 'destructive',
      });
    },
  });

  return {
    createPatientCase: createMutation.mutateAsync,
    updatePatientCase: updateMutation.mutateAsync,
    closePatientCase: closeMutation.mutateAsync,
    setReferredBackFlag: referredBackMutation.mutateAsync,
    addCaseActivity: activityMutation.mutateAsync,
    deletePatientCase: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isClosing: closeMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isFlippingReferredBack: referredBackMutation.isPending,
    isAddingActivity: activityMutation.isPending,
  };
};
